import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ArrowRight, Clock, GraduationCap, CheckCircle2, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SafeImage from '@/components/SafeImage';

export default function Programs() {
  const [programs, setPrograms] = useState([]);
  useEffect(() => { api.get('/programs').then(({ data }) => setPrograms(data)); }, []);

  const live = programs.filter(p => !p.is_coming_soon);
  const coming = programs.filter(p => p.is_coming_soon);

  return (
    <div data-testid="programs-page" className="bg-trjlight">
      {/* HERO BAND — Available for Enrollment */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 hero-grain opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-12">
          <span className="trj-divider">Now Enrolling</span>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight">
            Available for <span className="text-gold font-serif italic">Enrollment</span>
          </h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl leading-relaxed">
            Enroll today in TRJ Academy's current professional training and certification programs.
            Pay once, gain immediate access, and earn a verifiable certificate on completion.
          </p>
        </div>
      </section>

      {/* LIVE PROGRAMS */}
      <section className="relative -mt-10 pb-20" data-testid="available-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6">
            {live.map((p) => (
              <Link key={p.program_id} to={`/programs/${p.slug}`}
                data-testid={`program-card-${p.slug}`}
                className="card-elevate group bg-white border-2 border-gold/40 rounded-xl overflow-hidden flex flex-col shadow-xl">
                <div className="aspect-[16/9] overflow-hidden bg-navy/5 relative">
                  <SafeImage src={p.image_url} alt={p.title}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-3 left-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-navy bg-gold px-2.5 py-1 rounded">Enroll Today</span>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold">{p.school}</div>
                  <h3 className="font-display font-semibold text-xl text-navy mt-2 leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.short_description}</p>

                  <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-navy/70" /> {p.duration_hours} hrs</span>
                    <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5 text-navy/70" /> {p.level}</span>
                    <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5 text-gold" /> Certificate</span>
                  </div>

                  {p.outcomes && p.outcomes.length > 0 && (
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
                      {p.outcomes.slice(0, 3).map(o => (
                        <li key={o} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-trjgreen mt-0.5 shrink-0" /><span>{o}</span></li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Tuition</div>
                      <div className="font-serif text-3xl text-navy">${p.price_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                    <Button className="btn-gold h-10" data-testid={`enroll-cta-${p.slug}`}>
                      Enroll Now <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COMING SOON */}
      {coming.length > 0 && (
        <section className="py-16 bg-white border-t border-slate-200" data-testid="coming-soon-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="trj-divider !text-navy/70">Future Programs</span>
              <h2 className="font-display font-semibold text-3xl lg:text-4xl text-navy mt-4">Coming Soon</h2>
              <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
                Additional professional training and certification programs launching throughout 2026–2027.
                Join a waitlist to be notified — and to receive launch pricing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {coming.map((p) => (
                <div key={p.program_id}
                  data-testid={`coming-soon-${p.slug}`}
                  className="bg-trjlight border border-slate-200 rounded-lg p-5 flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-gold font-semibold">{p.school}</div>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-gold bg-gold/10 px-2 py-1 rounded">Coming Soon</span>
                  </div>
                  <h3 className="font-display font-semibold text-lg text-navy mt-3 leading-snug">{p.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 flex-1">Future professional training program — pre-enrollment opens soon.</p>
                  <Link to={`/programs/${p.slug}`} className="mt-4 text-sm font-semibold text-navy hover:text-gold inline-flex items-center" data-testid={`waitlist-link-${p.slug}`}>
                    Join Waitlist <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
